import React, { useState, useEffect, useCallback, useRef } from "react";
import publicAxios from "../utils/publicAxios";
import { FaEnvelope, FaPhoneAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";
import XeroLogo from "./../assets/images/xero-logo.png";
import MyobLogo from "./../assets/images/MYOB_Logo.png";
import QuickBookLogo from "./../assets/images/quickbooks.png";
import ReckonLogo from "./../assets/images/Reckon_Logo-1.png";
import NetworkStatus from "../utils/networkStatus";
import HeaderDemoAccountDashboard from "../components/layout/header/HeaderDemoAccountDashboard";
import DemoAccInfoModal from "../components/modals/DemoAccInfoModal";
import "./../assets/scss/home.scss";
import useMixpanelEventAction from "../config/mixpanel/useMixpanelEventAction";
import {
  HTTP_STATUS_SUCCESS,
  INFO_EMAIL,
  MAILTO_INFO_EMAIL,
  showToastType
} from "../utils/constants/appConstants";
import {
  auPhNumber,
  auPhNumberTag,
  auText,
  commonErrMsg,
  invcsConnect,
  nzPhNumber,
  nzPhNumberTag,
  nzText
} from "../locales/en";
import {
  getDemoinvoiceUrl,
  getDemoUserUrl
} from "../utils/constants/apiEndPoints";
import { DEMO_ACCOUNT_INVOICE } from "../route/paths";
import { showToast } from "../utils/utils";

const DemoAccountInvoiceConnect = () => {
  const navigate = useNavigate();
  const initialRender = useRef(true);
  const [demoInfoModal, setDemoInfoModal] = useState(true);
  const [showAlert, setShowAlert] = useState(false);
  const HEARTBEAT_INTERVAL = 15 * 60 * 1000;
  const MODAL_KEY = "demoInfoModalTimestamp";

  const [userId, setUserId] = useState(null);
  const { completeDemoAccountMixpanelEvent } = useMixpanelEventAction();

  // Check whether the modal should be shown based on the timestamp
  const checkModalVisibility = useCallback(() => {
    const lastClosedTimestamp = localStorage.getItem(MODAL_KEY);
    const currentTime = Date.now();

    if (
      !lastClosedTimestamp ||
      currentTime - parseInt(lastClosedTimestamp, 10) >= HEARTBEAT_INTERVAL
    ) {
      setDemoInfoModal(true);
      setShowAlert(false);
    } else {
      setDemoInfoModal(false);
      setShowAlert(true);
    }
  }, [HEARTBEAT_INTERVAL]);

  const handleHideInfoModal = () => {
    setDemoInfoModal(!demoInfoModal);
    setShowAlert(true);
    localStorage.setItem(MODAL_KEY, Date.now().toString());
  };

  // Retrieve user ID from localStorage (if already exists)
  const getUserIdFromStorage = () => {
    const storedData = localStorage.getItem("fundTap_demoUser_account_setup");
    if (storedData) {
      const parsedData = JSON.parse(storedData);
      return parsedData?.id || null;
    }
    return null;
  };

  const handleCreateDemoAccount = useCallback(async () => {
    // Check if demo user already exists in local storage
    const existingUser = localStorage.getItem("fundTap_demoUser_account_setup");

    if (existingUser) {
      return;
    }

    try {
      const response = await publicAxios.post(getDemoUserUrl);

      if (response?.data?.status === HTTP_STATUS_SUCCESS) {
        const userData = response.data.data;

        localStorage.setItem(
          "fundTap_demoUser_account_setup",
          JSON.stringify(userData)
        );
        completeDemoAccountMixpanelEvent(userData?.email);
        setUserId(userData?.id);
      } else {
        console.error(commonErrMsg?.failDemoUser, response?.data?.message);
      }
    } catch (error) {
      console.error("Error:", error.message);
    } finally {
      // finally
    }
  }, [completeDemoAccountMixpanelEvent]);

  const handleClickAccountingSoft = async () => {
    try {
      const response = await publicAxios.post(getDemoinvoiceUrl(userId));

      if (response?.data?.status === HTTP_STATUS_SUCCESS) {
        localStorage.setItem("invoice_redirected", "true");
        navigate(DEMO_ACCOUNT_INVOICE);
      }
    } catch (error) {
      showToast(error.response.data.errors, showToastType.error);
    }
  };

  useEffect(() => {
    checkModalVisibility();
    const heartbeat = setInterval(checkModalVisibility, HEARTBEAT_INTERVAL);
    return () => clearInterval(heartbeat);
  }, [HEARTBEAT_INTERVAL, checkModalVisibility]);

  useEffect(() => {
    if (initialRender.current) {
      handleCreateDemoAccount();
      initialRender.current = false;
    }
    // Always get user ID from storage (if it exists)
    const userId = getUserIdFromStorage();
    setUserId(userId);
  }, [handleCreateDemoAccount]);

  // Check on component mount
  useEffect(() => {
    const storedData = localStorage.getItem("fundTap_demoUser_account_setup");
    const redirected = localStorage.getItem("invoice_redirected");

    if (storedData && redirected === "true") {
      navigate(DEMO_ACCOUNT_INVOICE);
    }
  }, [navigate]);

  return (
    <>
      <HeaderDemoAccountDashboard />

      {/* <LoginAsUser /> */}

      <div className="home-main-section">
        {showAlert && (
          <div className="alert-message-wrap-blog mb-3">
            <div className="alert-message-wrap">
              <button
                className="close-alert-btn"
                onClick={() => setShowAlert(false)}
              >
                X
              </button>
              <p>{`Welcome to the FundTap Demo!`}</p>
              <p>{`First step is to connect your invoices by clicking on the accounting software you use (Don't worry, it won't actually connect in this demo).`}</p>
            </div>
          </div>
        )}
        <div className="container">
          <div className="home-title">
            <h1>{`Connect Your Invoices`}</h1>
            <p className="sub-title">{`Click on your accounting provider for instant access to FundTap`}</p>
          </div>

          <div className="accounting-software">
            <div className="row">
              <div className="col-md-8 offset-md-2">
                <div className="row g-5">
                  <div className="col-md-6 text-center">
                    <Link onClick={handleClickAccountingSoft}>
                      <img
                        src={XeroLogo}
                        alt="xero logo"
                        width="200"
                        height="200"
                      />
                    </Link>
                  </div>
                  <div className="col-md-6 text-center">
                    <Link onClick={handleClickAccountingSoft}>
                      <img
                        src={MyobLogo}
                        alt="Myob logo"
                        width="303"
                        height="200"
                      />
                    </Link>
                  </div>
                </div>
                <div className="row g-5">
                  <div className="col-md-6 text-center align-items-center d-flex">
                    <Link onClick={handleClickAccountingSoft}>
                      <img
                        src={QuickBookLogo}
                        alt="Quick book logo"
                        width="446"
                        height="72"
                      />
                    </Link>
                  </div>
                  <div className="col-md-6 text-center">
                    <Link onClick={handleClickAccountingSoft}>
                      <img
                        src={ReckonLogo}
                        alt="Reckon logo"
                        width="200"
                        height="200"
                      />
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="setup-section">
            <h3>{`Unsure how to get set up?`}</h3>
            <p>{`Chat to our onboarding specialists who can walk you through step-by-step.`}</p>

            <ul className="contact-section">
              <li>
                <span className="icon">
                  <FaEnvelope />
                </span>
                <span>
                  <Link to={MAILTO_INFO_EMAIL}>{INFO_EMAIL}</Link>
                </span>
              </li>
              <li>
                <span className="icon">
                  <FaPhoneAlt />
                </span>
                <span>
                  <p>
                    {auText}
                    <Link to={`tel:${auPhNumberTag}`}>{auPhNumber}</Link>
                  </p>
                  <p>
                    {nzText}
                    <Link to={`tel:${nzPhNumberTag}`}>{nzPhNumber}</Link>
                  </p>
                </span>
              </li>
            </ul>
          </div>

          <div className="privecy-text-wrap text-center">
            <p>{invcsConnect.wetakeUour}</p>
          </div>
        </div>
      </div>

      <DemoAccInfoModal isOpen={demoInfoModal} toggle={handleHideInfoModal} />

      <NetworkStatus />
    </>
  );
};

export default DemoAccountInvoiceConnect;
